import { AlertTriangle, Bell, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { laymanExplanation } from "@/lib/alertLayman";

const SEV_COLOR = {
  info: "var(--gold)",
  warning: "var(--rose-gold)",
  critical: "var(--accent-warning)",
};

export default function AlertsFeed({ alerts }) {
  return (
    <TooltipProvider delayDuration={150}>
      <div className="parchment-card rounded-2xl p-8 h-full flex flex-col" data-testid="alerts-feed">
        <div className="flex items-center justify-between mb-6">
          <div className="label-overline flex items-center gap-2 constellation-underline">
            <Bell size={12} /> Sky Alerts
          </div>
          <span className="label-overline">{alerts.length} active</span>
        </div>

        {/* Hint hint hint */}
        {alerts.length > 0 && (
          <p className="font-serif-italic text-[color:var(--text-muted)] text-xs mb-3 flex items-center gap-1.5">
            <Info size={11} /> Hover any alert — a plain-English note drops open underneath.
          </p>
        )}

        <div className="flex-1 overflow-hidden">
          {alerts.length === 0 ? (
            <div className="text-[color:var(--text-muted)] text-sm py-6 font-serif-italic">
              No alerts. The sky is quiet.
            </div>
          ) : (
            <ul className="space-y-4 max-h-[420px] overflow-y-auto pr-2">
              {alerts.slice(0, 10).map((a, i) => {
                const color = SEV_COLOR[a.severity] || SEV_COLOR.info;
                const layman = laymanExplanation(a);
                return (
                  <Tooltip key={a.id + i}>
                    <TooltipTrigger asChild>
                      <li
                        className="border-l-2 pl-4 py-1 rounded-r hover:bg-[color:var(--gold)]/[0.04] cursor-help focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--gold)]/60"
                        tabIndex={0}
                        style={{ borderColor: color, transition: "background 200ms ease" }}
                        data-testid={`alert-item-${i}`}
                        aria-label={`${a.severity} alert: ${a.title}. Layman's explanation: ${layman}`}
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle size={12} style={{ color }} />
                          <span
                            className="font-serif-italic text-xs uppercase tracking-widest"
                            style={{ color }}
                          >
                            {a.severity}
                          </span>
                          <Info
                            size={10}
                            className="ml-auto text-[color:var(--text-muted)] opacity-60"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="mt-1 text-[color:var(--parchment-soft)] text-base font-serif-display tracking-tight">
                          {a.title}
                        </div>
                        {a.issued_at && (
                          <div className="label-overline mt-1">
                            {new Date(a.issued_at).toLocaleString()}
                          </div>
                        )}
                      </li>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      sideOffset={8}
                      className="max-w-md w-[min(360px,80vw)] bg-[color:var(--bg-deeper)] border border-[color:var(--gold)]/40 text-[color:var(--parchment)] font-sans-body text-sm leading-relaxed p-4 shadow-xl"
                      data-testid={`alert-tooltip-${i}`}
                    >
                      <div className="label-overline mb-2 flex items-center gap-1.5" style={{ color }}>
                        <Info size={10} /> What this means in plain English
                      </div>
                      <p className="font-serif-italic text-[color:var(--parchment-soft)]">
                        {layman}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
